import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import ParticipantSelector from "./expenses/ParticipantSelector";
import type { Participant } from "./expenses/ParticipantSelector";
import { formatISO, parseISO } from "date-fns";

interface EditTodoModalProps {
  todo: {
    id: string;
    trip_id: string;
    title: string;
    description: string | null;
    phase: string;
    due_date?: string | null;
    required: boolean | null;
  };
  tripId: string;
  onClose: () => void;
  onUpdated: () => Promise<void>;
}

export function EditTodoModal({
  todo,
  tripId,
  onClose,
  onUpdated,
}: EditTodoModalProps) {
  const [title, setTitle] = useState(todo.title);
  const [description, setDescription] = useState(todo.description ?? "");
  const [phase, setPhase] = useState(todo.phase);
  const [required, setRequired] = useState(todo.required ?? false);
  const [dueDate, setDueDate] = useState(todo.due_date ?? "");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [distributeToAll, setDistributeToAll] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const { data: participantData, error: participantError } = await supabase
        .from("trip_participants")
        .select("user_id, profiles(first_name, last_name, email)")
        .eq("trip_id", tripId);

      if (participantError || !participantData) return;

      const mapped = participantData.map((entry: any) => ({
        user_id: entry.user_id,
        ...entry.profiles,
      }));

      setParticipants(mapped);

      // erst danach assignments laden
      const { data: assignmentData, error: assignmentError } = await supabase
        .from("trip_todo_assignments")
        .select("user_id")
        .eq("todo_id", todo.id);

      if (assignmentError || !assignmentData) return;

      const assignedUserIds = assignmentData.map((a) => a.user_id);
      setSelectedUsers(assignedUserIds);

      const allUserIds = mapped.map((p) => p.user_id);
      const assignedSet = new Set(assignedUserIds);
      const isToAll =
        allUserIds.length > 0 && allUserIds.every((id) => assignedSet.has(id));

      setDistributeToAll(isToAll);
    };

    fetch();
  }, [tripId, todo.id]);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const { error: updateError } = await supabase
        .from("trip_todos")
        .update({
          title,
          description,
          phase,
          required,
          due_date: dueDate
            ? formatISO(new Date(dueDate), { representation: "date" })
            : null,
        })
        .eq("id", todo.id);

      if (updateError) throw updateError;

      const { error: deleteError } = await supabase
        .from("trip_todo_assignments")
        .delete()
        .eq("todo_id", todo.id);

      if (deleteError) throw deleteError;

      const userIds = distributeToAll
        ? participants.map((p) => p.user_id)
        : selectedUsers;

      const { data: existingAssignments } = await supabase
        .from("trip_todo_assignments")
        .select("user_id")
        .eq("todo_id", todo.id);

      const existingUserIds = new Set(
        (existingAssignments ?? []).map((a) => a.user_id)
      );

      const newAssignments = userIds
        .filter((user_id) => !existingUserIds.has(user_id))
        .map((user_id) => ({
          todo_id: todo.id,
          user_id,
          status: "pending",
        }));

      if (newAssignments.length > 0) {
        const { error: insertError } = await supabase
          .from("trip_todo_assignments")
          .insert(newAssignments);

        if (insertError) throw insertError;
      }

      await onUpdated(); // ⬅️ warte, bis das Refetching abgeschlossen ist
      onClose(); // ⬅️ danach Modal sicher schließen
    } catch (e) {
      console.error("Fehler beim Aktualisieren:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md overflow-y-auto max-h-[90vh]">
        <h2 className="text-sm font-semibold text-slate-100 mb-5">Aufgabe bearbeiten</h2>

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Titel *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        />

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Beschreibung</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        />

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Phase</label>
        <select
          value={phase}
          onChange={(e) => setPhase(e.target.value as any)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        >
          <option value="before">Vor der Reise</option>
          <option value="during">Während der Reise</option>
          <option value="after">Nach der Reise</option>
        </select>

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Frist (optional)</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        />

        <label className="flex items-center gap-2 mb-3 text-xs text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={required}
            onChange={() => setRequired(!required)}
            className="accent-sky-500"
          />
          Muss-Aufgabe
        </label>

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Teilnehmer</label>
        <ParticipantSelector
          participants={participants}
          selected={selectedUsers}
          setSelected={setSelectedUsers}
          distributeToAll={distributeToAll}
          setDistributeToAll={setDistributeToAll}
        />

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading || !title}
            className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors disabled:opacity-50"
          >
            {loading ? "Speichern…" : "Aktualisieren"}
          </button>
        </div>
      </div>
    </div>
  );
}
